import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, PinIcon } from 'lucide-react';
// import services
import { getPinList } from '../services/user.service';
// import custom components
import DataTable from '../custom/DataTable';
import { dateTimeFormat } from '../lib/dateHelper';
// import components
import AssignPin from '../components/AssignPin';

const PinList = () => {

    // hooks
    const navigate = useNavigate();
    const columns = [
        {
            key: "pinNumber",
            text: "PIN Number",
            className: "text-left",
            align: "left",
            sortable: false,
            cell: ({ pinNumber }) => {
                return (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        {pinNumber || 'NULL'}
                    </span>
                )
            }
        },
        {
            key: "isUsed",
            text: "Used",
            className: "text-left",
            align: "left",
            sortable: false,
            cell: ({ isUsed }) => {
                return (
                    <span className={`px-3 py-1 bg-${isUsed ? 'green' : 'red'}-100 text-${isUsed ? 'green' : 'red'}-700 rounded-full text-sm font-medium`}>
                        {String(isUsed)}
                    </span>
                )
            }
        },
        {
            key: "createdAt",
            text: "Created At",
            className: "text-left",
            align: "left",
            sortable: false,
            cell: ({ createdAt }) => {
                return dateTimeFormat(createdAt);
            }
        },
        {
            key: "usedDate",
            text: "Used At",
            className: "text-left",
            align: "left",
            sortable: false,
            cell: ({ usedDate }) => {
                return usedDate ? dateTimeFormat(usedDate) : 'NULL';
            }
        }
    ];
    // state
    const [data, setData] = useState([]);
    const [count, setCount] = useState(0);
    const [loader, setLoader] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, search: "" });
    const [showModal, setShowModal] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            setLoader(true);
            const { status, result } = await getPinList(pagination);
            setLoader(false);
            if (status === true) {
                setData(result.data)
                setCount(result.count)
            }
        } catch (err) {
            setLoader(false);
        }
    }, [pagination])

    useEffect(() => {
        fetchData()
    }, [pagination])

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full">
                                <PinIcon className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Pin List</h1>
                                <p className="text-gray-600 text-sm">Manage and view all pin numbers</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-semibold transition-all hover:from-cyan-600 hover:to-blue-700 shadow-md hover:shadow-lg"
                            >
                                <User className="w-4 h-4" />
                                Assign Pin
                            </button>
                        </div>
                    </div>
                </div>
                <DataTable
                    columns={columns}
                    data={data}
                    count={count}
                    loader={loader}
                    setPagination={setPagination}
                />
            </div>
            <AssignPin
                showModal={showModal}
                setShowModal={setShowModal}
                fetchData={fetchData}
            />
        </div>
    );
};

export default PinList;